import axios from 'axios';
import {
  Complexity,
  KidsUserProfile,
  GrammarExercise,
  GrammarCheckResult,
  ReadingContent,
  ReadingCheckResult,
  ListeningContent,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// ========== User Profile API ==========

export const getUserProfile = async (userId: string): Promise<KidsUserProfile | null> => {
  try {
    const response = await apiClient.get(`/api/users/${userId}`);
    return response.data;
  } catch (error) {
    return null;
  }
};

export const updateUserProfile = async (userId: string, data: Partial<KidsUserProfile>): Promise<KidsUserProfile> => {
  const response = await apiClient.post('/api/users/profile', {
    userId,
    ...data,
    isKidsAccount: true,
  });
  return response.data;
};

// ========== Text-to-Speech API ==========

export type TTSVoice =
  | 'en-GB-SoniaNeural'
  | 'en-GB-RyanNeural'
  | 'en-US-JennyNeural'
  | 'en-US-GuyNeural';

export const synthesizeSpeech = async (text: string, voice: TTSVoice = 'en-US-JennyNeural'): Promise<Blob> => {
  const response = await apiClient.post('/api/speech/synthesize', { text, voice }, {
    responseType: 'blob',
  });
  return response.data;
};

// ========== Grammar API (Kids) ==========

export const getGrammarExercise = async (complexity: Complexity): Promise<GrammarExercise> => {
  const response = await apiClient.get(`/api/kids/grammar-exercise`, {
    params: { complexity },
  });
  return response.data;
};

export const checkGrammar = async (
  userText: string,
  expectedText: string,
  complexity: Complexity
): Promise<GrammarCheckResult> => {
  const response = await apiClient.post('/api/kids/grammar-check', {
    userText,
    expectedText,
    complexity,
  });
  return response.data;
};

// ========== Reading API (Kids) ==========

export const getReadingContent = async (complexity: Complexity): Promise<ReadingContent> => {
  const response = await apiClient.get('/api/kids/reading-content', {
    params: { complexity },
  });
  return response.data;
};

export const checkReadingAnswers = async (
  contentId: string,
  answers: number[]
): Promise<ReadingCheckResult> => {
  const response = await apiClient.post('/api/kids/reading-check', {
    contentId,
    answers,
  });
  return response.data;
};

// ========== Listening API (Kids) ==========

export const getListeningContent = async (complexity: Complexity): Promise<ListeningContent> => {
  const response = await apiClient.get('/api/kids/listening-content', {
    params: { complexity },
  });
  return response.data;
};

export const checkListeningAnswers = async (
  contentId: string,
  answers: number[]
): Promise<ReadingCheckResult> => {
  const response = await apiClient.post('/api/kids/listening-check', {
    contentId,
    answers,
  });
  return response.data;
};

// ========== Speaking API (uses existing endpoints) ==========

export interface ProAnalysis {
  transcribedText: string;
  accuracy: number;
  fluency: number;
  completeness: number;
  pronunciationScore: number;
  overallScore: number;
  wordAnalysis: Array<{
    target: string;
    spoken: string;
    status: 'correct' | 'incorrect' | 'partial' | 'missed' | 'extra';
    accuracyScore: number;
  }>;
  tips: string[];
}

export interface ProTranscriptionResult {
  text: string;
  words: Array<{ text: string; confidence: number }>;
  confidence: number;
  proAnalysis: ProAnalysis;
}

export const transcribeAudioPro = async (audioBlob: Blob, targetText: string): Promise<ProTranscriptionResult> => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  formData.append('targetText', targetText);

  const response = await apiClient.post<ProTranscriptionResult>('/api/transcribe/pro', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// ========== Speaking Exercises (Kids) ==========

export const getSpeakingExercise = async (complexity: Complexity): Promise<{ text: string; id: string }> => {
  const response = await apiClient.get('/api/kids/speaking-exercise', {
    params: { complexity },
  });
  return response.data;
};
